import { useQuery } from '@tanstack/react-query';
import Skeleton from '@/components/ui/custom/Skeleton';
import { useTranslation } from 'react-i18next';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { formatScheduledLessonText } from '@/utils/formatScheduledLessonText';
import { HomeworkStatistics } from '@/models/Statistics';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import Badge from '@/components/ui/custom/dataDisplay/Badge';
import { Link } from 'react-router-dom';
import { klassesQueryKeys } from '@/data/services/querykeys';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import Klass from '@/models/Klass';

interface PendingHomeworkItemProps {
  pendingHomework: HomeworkStatistics;
}

type PendingHomeworkContentProps = {
  pendingHomework: HomeworkStatistics;
  klass: Klass;
};

function PendingHomeworkContent({
  pendingHomework,
  klass,
}: PendingHomeworkContentProps) {
  const { pendingHomeworksCount, scheduledLesson } = pendingHomework;

  return (
    <div className="flex items-center justify-between w-full">
      <Text
        format="rubik-500"
        text={formatScheduledLessonText(scheduledLesson, klass)}
      />
      <div className="flex">
        <Badge count={pendingHomeworksCount} position="-top-0.5 right-0">
          <ClipboardDocumentListIcon className="w-6" />
        </Badge>
      </div>
    </div>
  );
}

export default function PendingHomeworkItem({
  pendingHomework,
}: PendingHomeworkItemProps) {
  const { scheduledLesson } = pendingHomework;
  const klassId = scheduledLesson.klass;
  const { t } = useTranslation('translation', {
    keyPrefix: 'teacherDashboard.pendingHomeworks',
  });
  const {
    data: klass,
    isLoading: loadingKlass,
    isError,
  } = useQuery({
    ...klassesQueryKeys.get(klassId),
  });

  if (loadingKlass) {
    return (
      <Skeleton className="bg-neutral-content opacity-25 rounded-md w-full h-4" />
    );
  } else if (isError || !klass) {
    return <Text text={t('klassNotFound')} />;
  }

  const hasLesson = !!scheduledLesson.lesson?.id;
  const klassUrl = `/courses/${klass.coursePathSlug}/lesson/${scheduledLesson.lesson?.id}/class/${klassId}`;
  const homeworkUrl = `activities/homework`;

  return (
    <Tooltip
      text={t('unassignedLessonLink')}
      enable={!hasLesson}
      color="gray"
      classNameContainer="w-full"
    >
      <ConditionalRenderer
        condition={hasLesson}
        fallback={
          <div className="text-inherit opacity-50 cursor-not-allowed">
            <PendingHomeworkContent
              pendingHomework={pendingHomework}
              klass={klass}
            />
          </div>
        }
      >
        <Link to={`${klassUrl}/${homeworkUrl}`} className="text-inherit">
          <PendingHomeworkContent
            pendingHomework={pendingHomework}
            klass={klass}
          />
        </Link>
      </ConditionalRenderer>
    </Tooltip>
  );
}
